import type { ComponentType } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconGallery } from '@hivebrite/design-system/Icon';

import { INITIAL_IMAGES } from 'hb-builder/components/BuilderComponents/page/block/Gallery/utils/constants';
import { Children } from 'hb-builder/components/common/Item';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { ChildrenRenderer } from 'hb-builder/components/Renderer/ChildrenRenderer';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useBuilderConfigContext,
  useComponentContext,
  useComponentsContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import { createNewItem } from 'hb-builder/utils/createNewItem';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { GalleryGridItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/galleryItemAttributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

import GridItem from '../components/GridItem';

const GalleryTemplateGrid: BuilderComponent<SectionItemAttributes> = () => (
  <div />
); // never used

interface GridUIProps {
  ChildrenComponent: ComponentType<{
    item: { children: DocumentItem[] };
    subdomainNetworkUrl?: string;
  }>;
  item: DocumentItem<SectionItemAttributes>;
  subdomainNetworkUrl?: string;
}

const GridUI = ({
  ChildrenComponent,
  item,
  subdomainNetworkUrl,
}: GridUIProps) => {
  const [
    firstImage,
    secondImage,
    thirdImage,
    fourthImage,
    fifthImage,
    sixthImage,
    seventhImage,
    eighthImage,
  ] = item.children || [];

  return (
    <StyledGridContainer data-testid="gallery-template-grid-component">
      <RowWrapper>
        <ChildrenComponent
          item={{ children: [firstImage] }}
          subdomainNetworkUrl={subdomainNetworkUrl}
        />
        <ColumnWrapper>
          <ChildrenComponent
            item={{ children: [secondImage] }}
            subdomainNetworkUrl={subdomainNetworkUrl}
          />
          <ChildrenComponent
            item={{ children: [thirdImage] }}
            subdomainNetworkUrl={subdomainNetworkUrl}
          />
        </ColumnWrapper>
        <ChildrenComponent
          item={{ children: [fourthImage] }}
          subdomainNetworkUrl={subdomainNetworkUrl}
        />
      </RowWrapper>

      <RowWrapper mt={{ s: 6, m: 5, l: 6 }}>
        <ColumnWrapper>
          <ChildrenComponent
            item={{ children: [fifthImage] }}
            subdomainNetworkUrl={subdomainNetworkUrl}
          />
          <ChildrenComponent
            item={{ children: [sixthImage] }}
            subdomainNetworkUrl={subdomainNetworkUrl}
          />
        </ColumnWrapper>
        <ChildrenComponent
          item={{ children: [seventhImage] }}
          subdomainNetworkUrl={subdomainNetworkUrl}
        />
        <ChildrenComponent
          item={{ children: [eighthImage] }}
          subdomainNetworkUrl={subdomainNetworkUrl}
        />
      </RowWrapper>
    </StyledGridContainer>
  );
};

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();
  const {
    hivebrite: { subdomainNetworkUrl },
  } = useBuilderConfigContext();

  const { Section } = components;

  return (
    <Section.RenderBuilder attributes={item.attributes}>
      <GridUI
        ChildrenComponent={Children}
        item={item}
        subdomainNetworkUrl={subdomainNetworkUrl}
      />
    </Section.RenderBuilder>
  );
};

const Render = ({
  item = { id: '', type: '' },
}: RenderProps<DocumentItem<GalleryGridItemAttributes>>) => {
  const { components } = useComponentsContext();
  const { bodyWidth, isMobilePreview, subdomainNetworkUrl, workspaceConfig } =
    useRendererContext();
  const { Section } = components;

  return (
    <Section.Render
      bodyWidth={bodyWidth}
      components={components}
      isMobilePreview={isMobilePreview}
      item={item}
      workspaceConfig={workspaceConfig}
    >
      <GridUI
        ChildrenComponent={ChildrenRenderer}
        item={item}
        subdomainNetworkUrl={subdomainNetworkUrl}
      />
    </Section.Render>
  );
};

GalleryTemplateGrid.componentCategory = 'block';
GalleryTemplateGrid.componentFamily = 'Gallery';
GalleryTemplateGrid.componentIcon = () => IconGallery;
GalleryTemplateGrid.componentLabel = __('Gallery');
GalleryTemplateGrid.componentName = 'GalleryTemplateGrid';
GalleryTemplateGrid.componentTemplateLabel = __('Grid');
GalleryTemplateGrid.componentType = 'section';

GalleryTemplateGrid.canBeDoubleClicked = true;
GalleryTemplateGrid.canBeDragged = true;
GalleryTemplateGrid.canBeListed = true;
GalleryTemplateGrid.canBeSelected = true;
GalleryTemplateGrid.componentClickable = false;

GalleryTemplateGrid.item = ({ components = {}, features = {} }) => {
  const { Section } = components as unknown as {
    Section: BuilderComponent<SectionItemAttributes>;
  };

  return Section.item({
    children: INITIAL_IMAGES({ features }).map((image, index) =>
      createNewItem(
        GridItem.item({
          image,
          index,
          textColor: 'white',
        }),
      ),
    ),
    features: features
      ? {
          ...features,
          section: {
            ...features?.section,
            fullWidth: true,
            name: GalleryTemplateGrid.componentName,
            type: GalleryTemplateGrid.componentName,
          },
        }
      : undefined,
  });
};

GalleryTemplateGrid.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

GalleryTemplateGrid.SettingsTabs = defaultTabs;

GalleryTemplateGrid.Render = Render;
GalleryTemplateGrid.RenderBuilder = RenderBuilder;

export default GalleryTemplateGrid;

const StyledGridContainer = styled(Box).attrs(() => ({
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  py: 6,
  width: '100%',
}))``;

const RowWrapper = styled(Box).attrs(() => ({
  flexDirection: { s: 'column', m: 'row' },
  justifyContent: 'center',
  width: '100%',
}))``;

const ColumnWrapper = styled(Box).attrs(() => ({
  flexDirection: { s: 'row', m: 'column' },
  justifyContent: 'space-between',
  mr: { s: 0, m: 5, l: 6 },
  width: {
    s: '100%',
    m: 'calc(14% + 2px)',
    l: 'calc(14% + 6px)',
    xl: 'calc(14% + 17px)',
  },
}))``;
